import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, LOCALE_ID, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { DepartmentService } from '../../../../services/department/department.service';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../services/scope/scope-constants';
import { DepartmentError } from '../../../../pages/admin-dashboard/departments/departments.model';
import {
  arabicOnlyValidator,
  englishOnlyValidator,
  noLeadingTrailingSpacesValidator,
  noNumbersValidator,
} from '../departments.validators';

@Component({
  selector: 'app-departments-create-component',
  imports: [ReactiveFormsModule, ConfirmationDialog, SuccessfulNotification, HasScopeDirective],
  templateUrl: './departments-create-component.html',
  styleUrl: './departments-create-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartmentsCreateComponent {
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private departmentService = inject(DepartmentService);
  private destroyRef = inject(DestroyRef);

  readonly SCOPES = SCOPES;

  isArabic = computed(() => this.locale.startsWith('ar'));
  isLoading = signal(false);
  showConfirmation = signal(false);
  successMessage = signal<string | null>(null);
  errorMessage = signal('');
  duplicateField = signal<'titleAr' | 'titleEn' | null>(null);

  departmentForm = new FormGroup({
    titleAr: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(255),
        arabicOnlyValidator(),
        noLeadingTrailingSpacesValidator(),
        noNumbersValidator(),
      ],
    }),
    titleEn: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(255),
        englishOnlyValidator(),
        noLeadingTrailingSpacesValidator(),
        noNumbersValidator(),
      ],
    }),
    descriptionAr: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(4000)],
    }),
    descriptionEn: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(4000)],
    }),
  });

  readonly titleArControl = computed(() => this.departmentForm.controls.titleAr);
  readonly titleEnControl = computed(() => this.departmentForm.controls.titleEn);
  readonly descriptionArControl = computed(() => this.departmentForm.controls.descriptionAr);
  readonly descriptionEnControl = computed(() => this.departmentForm.controls.descriptionEn);

  submitForm(): void {
    this.departmentForm.markAllAsTouched();
    this.duplicateField.set(null);

    if (this.departmentForm.invalid) {
      return;
    }

    this.showConfirmation.set(true);
  }

  cancel(): void {
    this.router.navigate(['/admin/departments']);
  }

  confirmCreate(): void {
    if (this.isLoading()) {
      return;
    }

    this.isLoading.set(true);

    const data = this.departmentForm.getRawValue();

    const dto = {
      titleAr: data.titleAr.trim(),
      titleEn: data.titleEn.trim(),
      descriptionAr: data.descriptionAr.trim() || undefined,
      descriptionEn: data.descriptionEn.trim() || undefined,
    };

    const subscription = this.departmentService.createDepartment(dto).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.showConfirmation.set(false);
        this.errorMessage.set('');

        const departmentName = this.isArabic() ? dto.titleAr : dto.titleEn;
        this.successMessage.set(departmentName);

        setTimeout(() => {
          this.router.navigate(['/admin/departments']);
        }, 3000);
      },
      error: (error: DepartmentError) => {
        this.isLoading.set(false);
        this.duplicateField.set(null);

        const bodyErrors = error?.reqBodyErrors ?? [];

        const isDuplicateArabic = bodyErrors.some((item) => item.includes('titleAr') && this.isDuplicateError(item));

        const isDuplicateEnglish = bodyErrors.some((item) => item.includes('titleEn') && this.isDuplicateError(item));

        if (isDuplicateArabic) {
          this.duplicateField.set('titleAr');
        }

        if (isDuplicateEnglish) {
          this.duplicateField.set('titleEn');
        }

        const backendMessage = this.isArabic() ? error?.messageAr : error?.messageEn;

        if (isDuplicateArabic || isDuplicateEnglish) {
          this.departmentForm.markAllAsTouched();
          this.errorMessage.set('');
          return;
        }

        this.errorMessage.set(
          backendMessage ||
            (this.isArabic()
              ? 'حدث خطأ أثناء إنشاء الإدارة، برجاء المحاولة مرة أخرى.'
              : 'Something went wrong while creating the department.'),
        );
      },
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  private isDuplicateError(message: string): boolean {
    const value = (message ?? '').toLowerCase();

    return value.includes('duplicate') || value.includes('unique') || value.includes('already exist');
  }

  closeConfirmation(): void {
    if (this.isLoading()) {
      return;
    }

    this.showConfirmation.set(false);
  }
}
